import { useCallback, useReducer, useState } from 'react';
import { TablePreference } from '../../Table/TablePreference';
import { useTableState } from '../../Table/useTableState';
import { TreeTableState } from '../TreeTableState';
import { TreeTableProps } from '../types';
import { Node } from '../../Tree';

export const useTreeTableState = (
  initialExpandedNodes: ReadonlySet<string>,
  initialPreference?: TablePreference
) => {
  const [expandedNodes, setExpandedNodes] = useState<ReadonlySet<string>>(initialExpandedNodes);
  const [treeTableState, treeDispatch] = useReducer(
    TreeTableState.reducer,
    TreeTableState.init(initialPreference?.selectedRows)
  );
  const partiallySelectedRows = treeTableState.partiallySelectedRows;
  const { dispatch, ...tableState } = useTableState(initialPreference);

  const onExpand: TreeTableProps['onExpand'] = (node: Node, expanded: boolean) => {
    const nodes = new Set(expandedNodes);
    if (nodes.has(node.id) && !expanded) nodes.delete(node.id);
    else if (!nodes.has(node.id) && expanded) nodes.add(node.id);
    setExpandedNodes(nodes);
  };

  const setSelectAllRows = useCallback<NonNullable<TreeTableProps['setSelectAllRows']>>(
    (props) => {
      treeDispatch({ type: 'clearPartiallySelectedRows' });
      const setSelectAllRows = tableState.setSelectAllRows;
      setSelectAllRows(props);
    },
    [tableState.setSelectAllRows]
  );

  const setTreeSelection = useCallback<NonNullable<TreeTableProps['setTreeSelection']>>((props) => {
    treeDispatch({ type: 'selectRow', ...props });
  }, []);

  const setParentChildSelection = useCallback<
    NonNullable<TreeTableProps['setParentChildSelection']>
  >((props) => {
    treeDispatch({ type: 'parentChildSelection', ...props });
  }, []);

  return {
    expandedNodes,
    setExpandedNodes,
    onExpand,
    ...tableState,
    treeDispatch,
    setSelectAllRows,
    setTreeSelection,
    setParentChildSelection,
    partiallySelectedRows,
  };
};
